/**
 * CheckboxField component.
 *
 * A single consent-style checkbox. Its value is "true" when ticked and ""
 * when not, so InlineForm's required check blocks submit on an unticked
 * required box exactly as it does on an empty text field.
 */

import type { FormFieldDefinition } from "../../types";

export interface CheckboxFieldOptions {
  field: FormFieldDefinition;
  onChange: (name: string, value: string) => void;
}

export class CheckboxField {
  private readonly element: HTMLElement;
  private readonly inputEl: HTMLInputElement;
  private readonly errorEl: HTMLElement;

  constructor(private readonly options: CheckboxFieldOptions) {
    const { field } = options;

    this.element = document.createElement("div");
    this.element.className = "hana-form-field hana-form-field--checkbox";

    const labelEl = document.createElement("label");
    labelEl.className = "hana-form-field__label";
    labelEl.setAttribute("for", `hana-field-${field.name}`);

    this.inputEl = document.createElement("input");
    this.inputEl.type = "checkbox";
    this.inputEl.id = `hana-field-${field.name}`;
    this.inputEl.name = field.name;
    this.inputEl.setAttribute(
      "aria-label",
      field.label + (field.required ? " (required)" : ""),
    );
    if (field.required) {
      this.inputEl.setAttribute("aria-required", "true");
    }
    this.inputEl.checked = field.value === "true";
    this.inputEl.addEventListener("change", () => {
      this.clearError();
      this.options.onChange(field.name, this.getValue());
    });

    labelEl.appendChild(this.inputEl);
    labelEl.appendChild(document.createTextNode(` ${field.label}`));
    if (field.required) {
      const asterisk = document.createElement("span");
      asterisk.className = "hana-form-field__required";
      asterisk.textContent = " *";
      asterisk.setAttribute("aria-hidden", "true");
      labelEl.appendChild(asterisk);
    }
    this.element.appendChild(labelEl);

    this.errorEl = document.createElement("div");
    this.errorEl.className = "hana-form-field__error";
    this.errorEl.setAttribute("role", "alert");
    this.errorEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.errorEl);
  }

  getElement(): HTMLElement {
    return this.element;
  }

  getValue(): string {
    return this.inputEl.checked ? "true" : "";
  }

  setError(message: string): void {
    this.errorEl.textContent = message;
    this.inputEl.setAttribute("aria-invalid", "true");
  }

  clearError(): void {
    this.errorEl.textContent = "";
    this.inputEl.removeAttribute("aria-invalid");
  }

  setDisabled(disabled: boolean): void {
    this.inputEl.disabled = disabled;
  }

  focus(): void {
    this.inputEl.focus();
  }
}
